// Colour arithmetic for theme families: reading a seed, mixing two colours, adding alpha, and WCAG
// 2.x contrast. It writes colours as numbers and #rrggbb[aa] strings, never a literal, so the
// raw-colour rule has nothing to read here (spec web/themes-engine).

/** Red, green and blue 0 to 255, and alpha 0 to 1. */
export type Rgba = readonly [number, number, number, number];

const HEX = /^#([0-9a-f]{3}|[0-9a-f]{4}|[0-9a-f]{6}|[0-9a-f]{8})$/i;
const FUNCTION = /^rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)(?:[\s,/]+([\d.]+))?\s*\)$/i;

/** A seed or token colour as numbers; throws on anything else, so a bad seed fails its test. */
export function parseColor(value: string): Rgba {
  const hex = HEX.exec(value.trim());
  if (hex !== null) {
    const digits = hex[1] ?? "";
    const full = digits.length <= 4 ? [...digits].map((digit) => digit + digit).join("") : digits;
    const byte = (at: number) => Number.parseInt(full.slice(at, at + 2), 16);
    return [byte(0), byte(2), byte(4), full.length === 8 ? byte(6) / 255 : 1];
  }
  const fn = FUNCTION.exec(value.trim());
  if (fn !== null) {
    return [Number(fn[1]), Number(fn[2]), Number(fn[3]), fn[4] === undefined ? 1 : Number(fn[4])];
  }
  throw new Error(`Not a colour: ${value}`);
}

const byte = (channel: number) =>
  Math.round(Math.min(255, Math.max(0, channel)))
    .toString(16)
    .padStart(2, "0");

/** `#rrggbb`, or `#rrggbbaa` when the colour is not opaque. */
export function toHex([red, green, blue, alpha]: Rgba): string {
  const opaque = alpha >= 1;
  return `#${byte(red)}${byte(green)}${byte(blue)}${opaque ? "" : byte(alpha * 255)}`;
}

/** `weight` of `a` and the rest of `b`, both opaque: mix(a, b, 0.1) is a faint tint of `a` on `b`. */
export function mix(a: string, b: string, weight: number): string {
  const [r1, g1, b1] = parseColor(a);
  const [r2, g2, b2] = parseColor(b);
  const share = (x: number, y: number) => x * weight + y * (1 - weight);
  return toHex([share(r1, r2), share(g1, g2), share(b1, b2), 1]);
}

/** `amount` of the way to white (lighten) or black (darken). */
export function lighten(color: string, amount: number): string {
  const [red, green, blue] = parseColor(color);
  const up = (channel: number) => channel + (255 - channel) * amount;
  return toHex([up(red), up(green), up(blue), 1]);
}

export function darken(color: string, amount: number): string {
  const [red, green, blue] = parseColor(color);
  const down = (channel: number) => channel * (1 - amount);
  return toHex([down(red), down(green), down(blue), 1]);
}

/** The colour at `alpha` opacity, as #rrggbbaa. */
export function withAlpha(color: string, alpha: number): string {
  const [red, green, blue] = parseColor(color);
  return toHex([red, green, blue, alpha]);
}

function luminance([red, green, blue]: readonly number[]): number {
  const linear = (channel: number) => {
    const value = channel / 255;
    return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * linear(red ?? 0) + 0.7152 * linear(green ?? 0) + 0.0722 * linear(blue ?? 0);
}

/** WCAG 2.x contrast of `foreground` over an opaque `background`, 1 to 21. */
export function contrast(foreground: string, background: string): number {
  const under = parseColor(background);
  const top = parseColor(foreground);
  const alpha = top[3];
  const seen = [0, 1, 2].map((at) => (top[at] ?? 0) * alpha + (under[at] ?? 0) * (1 - alpha));
  const [high, low] = [luminance(seen), luminance(under)].sort((x, y) => y - x);
  return ((high ?? 0) + 0.05) / ((low ?? 0) + 0.05);
}

/** `foreground` seen over an opaque `background`, as an opaque colour. */
export function over(foreground: string, background: string): string {
  const [red, green, blue, alpha] = parseColor(foreground);
  const [r2, g2, b2] = parseColor(background);
  const seen = (top: number, under: number) => top * alpha + under * (1 - alpha);
  return toHex([seen(red, r2), seen(green, g2), seen(blue, b2), 1]);
}

/** WCAG relative luminance, 0 (black) to 1 (white). */
export function relativeLuminance(colour: string): number {
  return luminance(parseColor(colour));
}
